import { useState, type CSSProperties, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Icon } from '../../components/Icon';
import { Logo } from '../../components/Logo';
import { api } from '../../lib/api';
import { date, dateTime } from '../../lib/format';

interface PublicOfferPage {
  tenantName: string;
  logoUrl: string | null;
  brandColor: string | null;
  number: string;
  title: string;
  body: string;
  startDate: string | null;
  expiresOn: string | null;
  status: string;
  statusLabel: string;
  answerable: boolean;
  candidateName: string;
  acceptedName: string | null;
  decidedAt: string | null;
}

/**
 * عرضُ العمل كما يقرؤه المرشّح.
 *
 * صفحةُ الشركة أمام من عرضت عليه العمل: بشعارها ولونها، بلا شريطٍ ولا
 * قائمة. ومن يفتحها ليس مستخدمًا في النظام — الرمزُ في الرابط هو ما
 * يعرّفه، ولا حسابَ ولا كلمة سرّ.
 *
 * والقبولُ يُطلب معه الاسم مكتوبًا بيده: القبولُ التزامٌ من الطرفين، وما
 * يُثبته النظام يُسأل عنه بعد شهور — واسمٌ منسوخٌ من الملفّ ليس توقيعًا.
 */
export function PublicOffer() {
  const { token = '' } = useParams();
  const base = `/public/offers/${encodeURIComponent(token)}`;

  const page = useQuery({
    queryKey: ['offer', token],
    queryFn: () => api<PublicOfferPage>(base, { skipAuth: true }),
    retry: false,
  });

  if (page.isPending) {
    return (
      <Frame>
        <div className="skeleton" style={{ height: 260 }} />
      </Frame>
    );
  }

  if (page.isError) {
    return (
      <Frame>
        <div className="book-done">
          <h1 className="book-title">الرابط لم يعد يعمل</h1>
          <p className="mute">
            {page.error instanceof Error
              ? page.error.message
              : 'هذا العرض غير موجود أو لم يعد متاحًا.'}
          </p>
        </div>
      </Frame>
    );
  }

  return (
    <OfferScreen
      info={page.data}
      submit={(body) =>
        api<{ ok: true; accepted: boolean }>(base, {
          method: 'POST',
          skipAuth: true,
          body,
        })
      }
    />
  );
}

function OfferScreen({
  info,
  submit,
}: {
  info: PublicOfferPage;
  submit: (body: Record<string, unknown>) => Promise<{ accepted: boolean }>;
}) {
  const [name, setName] = useState('');
  const [reason, setReason] = useState('');
  const [declining, setDeclining] = useState(false);
  const [done, setDone] = useState<boolean | null>(null);

  const brand = info.brandColor
    ? ({
        ['--book-brand' as string]: info.brandColor,
        ['--book-brand-ink' as string]: readableOn(info.brandColor),
      } as CSSProperties)
    : undefined;

  const answer = useMutation({
    mutationFn: (accept: boolean) =>
      submit(
        accept
          ? { answer: 'ACCEPT', name: name.trim() }
          : { answer: 'DECLINE', reason: reason.trim() || null },
      ),
    onSuccess: (r) => setDone(r.accepted),
  });

  const settled = done !== null || !info.answerable;

  return (
    <Frame brandStyle={brand} wide>
      <header className="book-brand">
        {info.logoUrl ? (
          <img className="book-logo" src={info.logoUrl} alt={info.tenantName} />
        ) : (
          <span className="book-logo book-logo-fallback">
            {info.tenantName.slice(0, 2)}
          </span>
        )}
        <div>
          <div className="book-tenant">{info.tenantName}</div>
          <h1 className="book-title">عرض عمل — {info.title}</h1>
          <div className="tiny mute num">{info.number}</div>
        </div>
      </header>

      <article className="rc-letter">{info.body}</article>

      {done === true && (
        <div className="book-done">
          <span className="book-done-mark" aria-hidden="true">
            <Icon name="check" size={22} />
          </span>
          <h2 className="book-title">سُجّل قبولك</h2>
          <p className="mute">
            سنتواصل معك لاستكمال العقد وإجراءات التعيين. ولا حاجة إلى فعل
            شيءٍ آخر الآن.
          </p>
        </div>
      )}

      {done === false && (
        <div className="book-done">
          <h2 className="book-title">سُجّل ردّك</h2>
          <p className="mute">شكرًا لوقتك، ونتمنّى لك التوفيق.</p>
        </div>
      )}

      {done === null && !info.answerable && (
        <div className="book-done">
          <h2 className="book-title">{info.statusLabel}</h2>
          <p className="mute">
            {info.acceptedName
              ? `قُبل هذا العرض باسم ${info.acceptedName} في ${dateTime(info.decidedAt)}.`
              : 'هذا العرض لم يعد يقبل ردًّا. راجع من راسلك.'}
          </p>
        </div>
      )}

      {!settled && (
        <section className="mt-4">
          {info.expiresOn && (
            <p className="tiny mute">
              مهلة الردّ حتى {date(info.expiresOn)}.
            </p>
          )}

          {!declining ? (
            <>
              <div className="field">
                <label className="label req" htmlFor="of-name">
                  اكتب اسمك كاملًا لتأكيد القبول
                </label>
                <input
                  id="of-name"
                  className="input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={info.candidateName}
                />
                <span className="tiny mute">
                  يُسجَّل اسمُك مع تاريخ القبول ويُحفظ في ملفّك.
                </span>
              </div>

              {answer.isError && (
                <p className="tiny" style={{ color: 'var(--danger)' }}>
                  {answer.error instanceof Error
                    ? answer.error.message
                    : 'تعذّر التسجيل. حاول بعد قليل.'}
                </p>
              )}

              <div className="row mt-4" style={{ gap: 'var(--sp-2)' }}>
                <button
                  className="btn btn-primary btn-lg"
                  onClick={() => answer.mutate(true)}
                  disabled={name.trim().length < 2 || answer.isPending}
                >
                  {answer.isPending ? 'جارٍ التسجيل…' : 'أقبل العرض'}
                </button>
                <button className="btn" onClick={() => setDeclining(true)}>
                  أعتذر
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="field">
                <label className="label" htmlFor="of-reason">
                  إن أحببت أن تذكر السبب
                </label>
                <textarea
                  id="of-reason"
                  className="input"
                  rows={3}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="اختياريّ — ويساعدنا في العرض القادم."
                />
              </div>

              <div className="row mt-4" style={{ gap: 'var(--sp-2)' }}>
                <button
                  className="btn btn-danger"
                  onClick={() => answer.mutate(false)}
                  disabled={answer.isPending}
                >
                  {answer.isPending ? 'جارٍ التسجيل…' : 'أرسل الاعتذار'}
                </button>
                <button className="btn" onClick={() => setDeclining(false)}>
                  رجوع
                </button>
              </div>
            </>
          )}
        </section>
      )}
    </Frame>
  );
}

function Frame({
  children,
  brandStyle,
  wide,
}: {
  children: ReactNode;
  brandStyle?: CSSProperties;
  wide?: boolean;
}) {
  return (
    <div className="book-page" style={brandStyle}>
      <div className={`book-card${wide ? ' rc-apply-card' : ''}`}>
        {children}
      </div>
      <p className="book-foot tiny mute">
        <Logo size={16} /> GoyOne
      </p>
    </div>
  );
}

/** لونُ الخطّ فوق لون الشركة — يُحسب من السطوع لا يُفترض أبيض. */
function readableOn(hex: string): string {
  const n = Number.parseInt(hex.slice(1), 16);
  const [r, g, b] = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  }) as [number, number, number];
  const L = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return L > 0.45 ? '#12214a' : '#ffffff';
}
